import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Grid,
    Card,
    CardContent,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Container,
    Pagination,
    Stack,
    Divider,
    Chip,
    Link
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Refresh as RefreshIcon,
    GetApp as GetAppIcon,
    Visibility as VisibilityIcon,
    Visibility as ViewIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import performaIncoieService from '../../../services/performaIncoieService';
import feesService from '../../../services/feesService';

const UnpaidInvoice = () => {
    // State management
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [labTypes, setLabTypes] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [invoiceDataApi, setInvoiceDataApi] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        labTypeId: '',
        transactionTypeId: '',
        userName: '',
        orderId: '',
        financialYear: '',
        dateFrom: null,
        dateTo: null,
        approvalDateFrom: null,
        approvalDateTo: null
    });

    const financialYears = [
        { value: '26', label: '2026-2027' },
        { value: '25', label: '2025-2026' },
        { value: '24', label: '2024-2025' },
        { value: '23', label: '2023-2024' },
        { value: '22', label: '2022-2023' },
        { value: '21', label: '2021-2022' },
        { value: '20', label: '2020-2021' },
        { value: '19', label: '2019-2020' }
    ];

    // Load initial data
    useEffect(() => {
        loadInitialData();
        fetchUnpaidInvoices();
    }, []);

    // Fetch data when page changes
    useEffect(() => {
        fetchUnpaidInvoices();
    }, [currentPage]);

    const loadInitialData = async () => {
        try {
            console.log('Loading initial dropdown data...');
            const [labTypesRes, transactionTypesRes] = await Promise.all([
                performaIncoieService.getLabTypes(),
                performaIncoieService.getTransactionTypes()
            ]);

            console.log('Lab Types Response:', labTypesRes);
            console.log('Transaction Types Response:', transactionTypesRes);

            if (labTypesRes.success) {
                setLabTypes(labTypesRes.data || []);
            } else {
                // Fallback data for lab types
                setLabTypes([
                    { id: 1, name: 'Testing' },
                    { id: 2, name: 'Calibration' },
                    { id: 3, name: 'Medical' }
                ]);
            }

            if (transactionTypesRes.success) {
                setTransactionTypes(transactionTypesRes.data || []);
            } else {
                // Fallback data for transaction types
                setTransactionTypes([
                    { id: 1, description: 'Assessment Fee' },
                    { id: 5, description: 'Calibration Fee' },
                    { id: 6, description: 'Medical Fee' }
                ]);
            }
        } catch (error) {
            console.error('Error loading initial data:', error);

            // Set fallback data if API calls fail
            setLabTypes([
                { id: 1, name: 'Testing' },
                { id: 2, name: 'Calibration' },
                { id: 3, name: 'Medical' }
            ]);

            setTransactionTypes([
                { id: 1, description: 'Assessment Fee' },
                { id: 5, description: 'Calibration Fee' },
                { id: 6, description: 'Medical Fee' }
            ]);

            toast.warning('Using default dropdown options - some API services may be unavailable');
        }
    };

    const fetchUnpaidInvoices = async () => {
        setLoading(true);
        try {
            const filterParams = {
                ...filters,
                dateFrom: filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : '',
                dateTo: filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : '',
                approvalDateFrom: filters.approvalDateFrom ? format(filters.approvalDateFrom, 'yyyy-MM-dd') : '',
                approvalDateTo: filters.approvalDateTo ? format(filters.approvalDateTo, 'yyyy-MM-dd') : '',
                page: currentPage,
                pageSize
            };

            console.log('Fetching unpaid invoices with params:', filterParams);
            const response = await performaIncoieService.getUnpaidInvoices(filterParams);
            console.log('API Response:', response);

            if (response.success) {
                setInvoices(response.data?.records || []);
                setTotalRecords(response.data?.totalRecords || 0);
                console.log('Data loaded successfully:', response.data?.records?.length, 'records');
            } else {
                toast.error(response.message || 'Failed to fetch unpaid invoices');
                setInvoices([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching unpaid invoices:', error);
            toast.error('Failed to fetch unpaid invoices: ' + (error.message || 'Unknown error'));
            setInvoices([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = () => {
        console.log('Search button clicked with filters:', filters);
        setCurrentPage(1);
        fetchUnpaidInvoices();
    };

    const handleDateSearch = () => {
        if (!filters.dateFrom || !filters.dateTo) {
            toast.warning('Please select both Date From and Date To');
            return;
        }
        setCurrentPage(1);
        fetchUnpaidInvoices();
    };

    const handleApprovalDateSearch = () => {
        if (!filters.approvalDateFrom || !filters.approvalDateTo) {
            toast.warning('Please select both Approval Date From and Approval Date To');
            return;
        }
        setCurrentPage(1);
        fetchUnpaidInvoices();
    };

    const handleResetFilter = () => {
        setFilters({
            labTypeId: '',
            transactionTypeId: '',
            userName: '',
            orderId: '',
            financialYear: '',
            dateFrom: null,
            dateTo: null,
            approvalDateFrom: null,
            approvalDateTo: null
        });
        setCurrentPage(1);
        setTimeout(() => {
            fetchUnpaidInvoices();
        }, 100);
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const filterParams = {
                ...filters,
                dateFrom: filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : '',
                dateTo: filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : '',
                approvalDateFrom: filters.approvalDateFrom ? format(filters.approvalDateFrom, 'yyyy-MM-dd') : '',
                approvalDateTo: filters.approvalDateTo ? format(filters.approvalDateTo, 'yyyy-MM-dd') : ''
            };

            const response = await performaIncoieService.exportUnpaidInvoices(filterParams);
            if (response.success) {
                toast.success('Export completed successfully');
            }
        } catch (error) {
            console.error('Error exporting data:', error);
            toast.error('Failed to export data');
        } finally {
            setExporting(false);
        }
    };

    const convertRelativePathToUrl = (path) => {
        if (!path) return '';

        // Convert ASP.NET style relative paths (~/) to proper URLs
        if (path.startsWith('~/')) {
            const orgId = localStorage.getItem('orgId') || ''; // Use stored orgId or default
            const baseUrl = 'https://nablwpnew.qci.org.in';
            const cleanPath = path.replace('~/', '/');

            // Format: https://nablwpnew.qci.org.in+path+&OrgId=67845
            const finalUrl = `${baseUrl}${cleanPath}&OrgId=${orgId}`;
            console.log('Converting path:', path, 'to URL:', finalUrl);
            return finalUrl;
        }
        return path;
    };
    const handlePerformaInvoice = (AppId, PaymentTypeId) => {


        const previewUrl = `https://nablwpnew.qci.org.in/InvoiceApplicationPerfomaQR?appId=${AppId}&payType=${PaymentTypeId}`;

        // Open in new tab/window
        window.open(previewUrl, '_blank');

    };

    const handlePerformaInvoiceAafee = async (AppId, PaymentTypeId) => {

        let currentInvoiceData = invoiceDataApi;
        if (!currentInvoiceData && PaymentTypeId === 7) {

            try {
                const response = await feesService.getAssessmentPerformaInvoice(AppId);
                if (response.status === 'success' && response.data && response.data.invoiceData) {
                    currentInvoiceData = response.data.invoiceData;
                    setInvoiceDataApi(currentInvoiceData); // Update state for future use

                }
            } catch (error) {
                console.warn('Failed to load invoice data, proceeding without ID:', error);
            }
        }

        // Get invoice data ID if available
        const invoiceId = currentInvoiceData?.Id || '';

        const orgId = localStorage.getItem('orgId')

        // Build URL with invoice ID if available
        let previewUrl = `https://nablwpnew.qci.org.in/InvoiceAAFEEPerforma?appId=${AppId}&payType=${PaymentTypeId}&Form11=1&OrgId=${orgId}`;
        if (invoiceId) {
            previewUrl += `&id=${invoiceId}`;
        }


        // Open in new tab/window
        window.open(previewUrl, '_blank');

    };

    const handlePerformaInvoiceAssessment = (AppId, id) => {

        const orgId = localStorage.getItem('orgId')

        const previewUrl = `https://nablwpnew.qci.org.in/UnpaidInvoiceAssessment.aspx?appId=${AppId}&payType=6&OrgId=${orgId}&id=${id}`;
        window.open(previewUrl, '_blank');
    };

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return format(date, 'dd/MM/yyyy');
    };

    const totalPages = Math.ceil(totalRecords / pageSize);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Proforma Invoice
                        </Typography>
                        <Divider />
                    </Box>

                    {/* Filter Section */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <FilterListIcon sx={{ mr: 1 }} />
                                <Typography variant="h6" component="h2">
                                    Filter
                                </Typography>
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={handleResetFilter}
                                        sx={{ mr: 1 }}
                                    >
                                        Reset Filter
                                    </Button>
                                    {/* <Button
                                        variant="contained"
                                        startIcon={<GetAppIcon />}
                                        onClick={handleExportExcel}
                                        disabled={exporting}
                                    >
                                        {exporting ? <CircularProgress size={20} /> : 'Download Excel'}
                                    </Button> */}
                                </Box>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Transaction Type</InputLabel>
                                        <Select
                                            value={filters.transactionTypeId}
                                            label="Transaction Type"
                                            onChange={(e) => handleFilterChange('transactionTypeId', e.target.value)}
                                        >
                                            <MenuItem value="">--Select Transaction Type--</MenuItem>
                                            {transactionTypes.map((type) => (
                                                <MenuItem key={type.id} value={type.id}>
                                                    {type.description || type.Description}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Order Id"
                                        placeholder="Enter Order Id"
                                        value={filters.orderId}
                                        onChange={(e) => handleFilterChange('orderId', e.target.value)}
                                    />
                                </Grid> */}

                                {/* <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Lab Type</InputLabel>
                                        <Select
                                            value={filters.labTypeId}
                                            label="Lab Type"
                                            onChange={(e) => handleFilterChange('labTypeId', e.target.value)}
                                        >
                                            <MenuItem value="">--Select Lab Type--</MenuItem>
                                            {labTypes.map((type) => (
                                                <MenuItem key={type.id} value={type.id}>
                                                    {type.name || type.Name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid> */}

                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        fullWidth
                                        label="User Name"
                                        placeholder="Enter UserName"
                                        value={filters.userName}
                                        onChange={(e) => handleFilterChange('userName', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Financial Year</InputLabel>
                                        <Select
                                            value={filters.financialYear}
                                            label="Financial Year"
                                            onChange={(e) => handleFilterChange('financialYear', e.target.value)}
                                        >
                                            <MenuItem value="">--Select Financial Year--</MenuItem>
                                            {financialYears.map((year) => (
                                                <MenuItem key={year.value} value={year.value}>
                                                    {year.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSearch}
                                        startIcon={<SearchIcon />}
                                        fullWidth
                                        sx={{ height: '56px' }}
                                    >
                                        Search
                                    </Button>
                                </Grid>
                            </Grid>

                            {/* Date Range Filter */}
                            <Card sx={{ mt: 2, border: '1px solid #0094ff' }}>
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DatePicker
                                                label="Date From"
                                                value={filters.dateFrom}
                                                onChange={(newValue) => handleFilterChange('dateFrom', newValue)}
                                                renderInput={(params) => <TextField {...params} fullWidth />}
                                                maxDate={new Date()}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DatePicker
                                                label="Date To"
                                                value={filters.dateTo}
                                                onChange={(newValue) => handleFilterChange('dateTo', newValue)}
                                                renderInput={(params) => <TextField {...params} fullWidth />}
                                                maxDate={new Date()}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Button
                                                variant="contained"
                                                onClick={handleDateSearch}
                                                fullWidth
                                            >
                                                Search
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Approval Date Range Filter */}
                            {/* <Card sx={{ mt: 2, border: '1px solid #0094ff' }}>
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DatePicker
                                                label="Approval Date From"
                                                value={filters.approvalDateFrom}
                                                onChange={(newValue) => handleFilterChange('approvalDateFrom', newValue)}
                                                renderInput={(params) => <TextField {...params} fullWidth />}
                                                maxDate={new Date()}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DatePicker
                                                label="Approval Date To"
                                                value={filters.approvalDateTo}
                                                onChange={(newValue) => handleFilterChange('approvalDateTo', newValue)}
                                                renderInput={(params) => <TextField {...params} fullWidth />}
                                                maxDate={new Date()}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Button
                                                variant="contained"
                                                onClick={handleApprovalDateSearch}
                                                fullWidth
                                            >
                                                Search
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card> */}
                        </CardContent>
                    </Card>

                    {/* Pagination */}
                    {totalRecords > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <Stack spacing={2}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                />
                                <Typography variant="body2" textAlign="center">
                                    Page {currentPage} of {totalPages} (Total Records: {totalRecords})
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    {/* Data Table */}
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress size={40} />
                            </Box>
                        ) : invoices.length > 0 ? (
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ minWidth: 200 }}><strong>Actions & Info</strong></TableCell>
                                        <TableCell sx={{ minWidth: 300 }}><strong>Laboratory Details</strong></TableCell>
                                        <TableCell sx={{ minWidth: 250 }}><strong>Contact Details</strong></TableCell>
                                        <TableCell sx={{ minWidth: 250 }}><strong>Address Details</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.map((invoice, index) => (
                                        <TableRow key={invoice.AppId || index} hover>
                                            <TableCell sx={{ minWidth: 200 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        <strong>S. No:</strong> {(currentPage - 1) * pageSize + index + 1}
                                                    </Typography>
                                                    {invoice.paths && (
                                                        <Link
                                                            href={convertRelativePathToUrl(invoice.paths)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<VisibilityIcon />}
                                                                sx={{ mt: 0.5 }}
                                                            >
                                                                View Invoice
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {invoice.TADAProcess === 1 && (
                                                        <Link
                                                            href={convertRelativePathToUrl(invoice.TADApath)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<VisibilityIcon />}
                                                                sx={{ mt: 0.5 }}
                                                            >
                                                                View TADA Invoice
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {invoice.PaymentTypeId === 2 ? (
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<ViewIcon />}
                                                            onClick={() => handlePerformaInvoice(invoice.AppId, invoice.PaymentTypeId)}
                                                            disabled={loading}
                                                            size="small"
                                                            color="secondary"
                                                            sx={{
                                                                borderColor: 'secondary.main',
                                                                '&:hover': {
                                                                    borderColor: 'secondary.dark',
                                                                    backgroundColor: 'secondary.50'
                                                                }
                                                            }}
                                                        >
                                                            Proforma Invoice
                                                        </Button>
                                                    ) : invoice.PaymentTypeId === 7 ? (
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<ViewIcon />}
                                                            onClick={() => handlePerformaInvoiceAafee(invoice.AppId, invoice.PaymentTypeId)}
                                                            disabled={loading}
                                                            size="small"
                                                            color="secondary"
                                                            sx={{
                                                                borderColor: 'secondary.main',
                                                                '&:hover': {
                                                                    borderColor: 'secondary.dark',
                                                                    backgroundColor: 'secondary.50'
                                                                }
                                                            }}
                                                        >
                                                            Proforma Invoice
                                                        </Button>
                                                    ) : invoice.PaymentTypeId === 6 ? (
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<ViewIcon />}
                                                            onClick={() => handlePerformaInvoiceAssessment(invoice.AppId, invoice.id)}
                                                            size="small"
                                                            color="secondary"
                                                            sx={{
                                                                borderColor: 'secondary.main',
                                                                '&:hover': {
                                                                    borderColor: 'secondary.dark',
                                                                    backgroundColor: 'secondary.50'
                                                                }
                                                            }}
                                                        >
                                                            Proforma Invoice
                                                        </Button>
                                                    ) : null}

                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 300 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        <strong>InvoiceNo:</strong> {invoice.PerfomaNo || '-'}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        <strong>Invoice Date:</strong> {invoice.invoiceDatee || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Lab ID:</strong> {invoice.ApplicationNo || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Laboratory Name:</strong> {invoice.LaboratoryName || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>UserName:</strong> {invoice.loginname || '-'}
                                                    </Typography>
                                                    <Chip
                                                        label={invoice.LabType || '-'}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 250 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2">
                                                        <strong>Person:</strong> {invoice.ContactPerson || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Email:</strong> {invoice.ContactEmail || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Mobile:</strong> {invoice.ContactMobile || '-'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 250 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2">
                                                        <strong>Address:</strong> {invoice.Address || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>City:</strong> {invoice.City || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>State:</strong> {invoice.State || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Pincode:</strong> {invoice.Pin || '-'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No Data Found
                                </Typography>
                            </Box>
                        )}
                    </TableContainer>

                    {/* Bottom Pagination */}
                    {totalRecords > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Stack spacing={2}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                />
                                <Typography variant="body2" textAlign="center">
                                    Page {currentPage} of {totalPages} (Total Records: {totalRecords})
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </Paper>

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
            </Container>
        </LocalizationProvider>
    );
};

export default UnpaidInvoice;